import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react'
import { carriers as carriersApi, type Carrier } from '../api'

const SESSION_KEY = 'lngops_selected_vessel'

interface VesselContextValue {
  vessels: Carrier[]
  selectedVesselId: number | null
  setSelectedVesselId: (id: number | null) => void
  selectedVessel: Carrier | null
  loading: boolean
}

const VesselContext = createContext<VesselContextValue | null>(null)

export function VesselProvider({ children }: { children: ReactNode }) {
  const [vessels, setVessels] = useState<Carrier[]>([])
  const [selectedVesselId, setSelectedVesselIdRaw] = useState<number | null>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY)
      if (stored) return Number(stored)
    } catch { /* empty */ }
    return null
  })
  const [loading, setLoading] = useState(true)

  const setSelectedVesselId = useCallback((id: number | null) => {
    setSelectedVesselIdRaw(id)
    try {
      if (id != null) sessionStorage.setItem(SESSION_KEY, String(id))
      else sessionStorage.removeItem(SESSION_KEY)
    } catch { /* empty */ }
  }, [])

  useEffect(() => {
    carriersApi
      .myVessels()
      .then((data) => {
        setVessels(data)
        if (data.length > 0) {
          // If we have a stored ID, verify it exists in the fetched list
          setSelectedVesselIdRaw((prev) => {
            if (prev != null && data.some((v) => v.id === prev)) return prev
            // Default to first vessel
            const fallback = data[0].id
            try { sessionStorage.setItem(SESSION_KEY, String(fallback)) } catch { /* empty */ }
            return fallback
          })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const selectedVessel = useMemo(
    () => vessels.find((v) => v.id === selectedVesselId) || null,
    [vessels, selectedVesselId]
  )

  const value = useMemo<VesselContextValue>(
    () => ({ vessels, selectedVesselId, setSelectedVesselId, selectedVessel, loading }),
    [vessels, selectedVesselId, setSelectedVesselId, selectedVessel, loading]
  )

  return <VesselContext.Provider value={value}>{children}</VesselContext.Provider>
}

export function useVesselContext(): VesselContextValue {
  const ctx = useContext(VesselContext)
  if (!ctx) throw new Error('useVesselContext must be used within a VesselProvider')
  return ctx
}
